import React, { useState, useRef, useEffect } from 'react';

import {
  Input,
  LanguageList,
  NationalityAutoCompleteList,
  useT,
} from '@repo/common';
import PostSection from '../PostSection';
import usePlaceholder from '../usePlaceholder';
import PostRadioGroup from '../PostRadioGroup';
import TopikRadioGroup from '../TopikRadioGroup';
import PostCheckboxRow from '../PostCheckboxRow';
import PostToggleRow from '../PostToggleRow';
import KoreanSkillsList from './KoreanSkillsList';
import CareerLevelList from './CareerLevelList';

import { ReactComponent as InfoIcon } from '@repo/common/assets/icons/20px/icon_20px_info_filled.svg';
import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-top.svg';

const toNormalizedArray = (values = []) =>
  values.map((value) => String(value || '').trim()).filter(Boolean);

const isSameArray = (a = [], b = []) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const getSelectedRequiredLanguages = (flags, customLanguage) => {
  const selected = Object.entries(flags)
    .filter(([key, value]) => value && !['noSelection', 'other'].includes(key))
    .map(([key]) => key);

  if (flags.other && String(customLanguage || '').trim()) {
    selected.push(String(customLanguage).trim());
  }

  return selected;
};

const Requirements = ({
  textValues,
  setTextValues,
  errors,
  careerLevelOptions,
}) => {
  const t = useT();
  const ph = usePlaceholder();
  const [nationalities, setNationalities] = useState([
    { value: '', isOpen: false },
  ]);
  const [noNationalitySelection, setNoNationalitySelection] = useState(false);
  const [isKoreanSkillsOpen, setIsKoreanSkillsOpen] = useState(false);
  const [isCareerLevelOpen, setIsCareerLevelOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);

  const [requiredLanguages, setRequiredLanguages] = useState({
    english: false,
    japanese: false,
    chinese: false,
    other: false,
    noSelection: false,
  });
  const [otherLanguage, setOtherLanguage] = useState('');

  const refs = useRef({
    koreanSkills: null,
    nationality: null,
    careerLevel: null,
    language: null,
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        refs.current.koreanSkills &&
        !refs.current.koreanSkills.contains(event.target)
      ) {
        setIsKoreanSkillsOpen(false);
      }

      if (
        refs.current.nationality &&
        !refs.current.nationality.contains(event.target)
      ) {
        setNationalities((prev) =>
          prev.map((item) => ({
            ...item,
            isOpen: false,
          })),
        );
      }

      if (
        refs.current.careerLevel &&
        !refs.current.careerLevel.contains(event.target)
      ) {
        setIsCareerLevelOpen(false);
      }

      if (
        refs.current.language &&
        !refs.current.language.contains(event.target)
      ) {
        setIsLanguageOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const syncApplicantNationalities = (
    nextNationalities,
    isNoSelection = false,
  ) => {
    setNationalities(nextNationalities);
    if (
      nextNationalities.some(
        (item) => String(item.value || '').trim().length > 0,
      )
    ) {
      setNoNationalitySelection(false);
    }
    setTextValues((prev) => ({
      ...prev,
      noNationalitySelection: isNoSelection,
      applicantNationality: nextNationalities
        .map((item) => item.value)
        .filter((value) => String(value || '').trim()),
    }));
  };

  const syncRequiredLanguageValues = (
    nextFlags,
    nextOtherLanguage = otherLanguage,
  ) => {
    setRequiredLanguages(nextFlags);
    setOtherLanguage(nextOtherLanguage);
    setTextValues((prev) => ({
      ...prev,
      noRequiredLanguageSelection: Boolean(nextFlags.noSelection),
      requiredLanguage: getSelectedRequiredLanguages(
        nextFlags,
        nextOtherLanguage,
      ),
    }));
  };

  const handleLanguageToggle = (languageKey) => (e) => {
    const isChecked = e.target.checked;
    if (languageKey === 'noSelection') {
      syncRequiredLanguageValues(
        {
          english: false,
          japanese: false,
          chinese: false,
          other: false,
          noSelection: isChecked,
        },
        '',
      );
      return;
    }

    const nextFlags = {
      ...requiredLanguages,
      [languageKey]: isChecked,
      noSelection: false,
    };

    syncRequiredLanguageValues(
      nextFlags,
      languageKey === 'other' && !isChecked ? '' : otherLanguage,
    );
  };

  useEffect(() => {
    const selectedValues = (textValues.requiredLanguage || [])
      .map((value) =>
        String(value || '')
          .trim()
          .toLowerCase(),
      )
      .filter(Boolean);

    const hasPresetLanguage =
      selectedValues.includes('english') ||
      selectedValues.includes('japanese') ||
      selectedValues.includes('chinese');
    const hasCustomOther = selectedValues.some(
      (value) => !['english', 'japanese', 'chinese'].includes(value),
    );
    const shouldKeepOtherOpen =
      requiredLanguages.other &&
      selectedValues.length === 0 &&
      !String(otherLanguage || '').trim();

    const nextFlags = {
      english: selectedValues.includes('english'),
      japanese: selectedValues.includes('japanese'),
      chinese: selectedValues.includes('chinese'),
      other: hasCustomOther || shouldKeepOtherOpen,
      noSelection:
        requiredLanguages.noSelection &&
        !hasPresetLanguage &&
        !hasCustomOther &&
        !shouldKeepOtherOpen,
    };

    const nextOther =
      (textValues.requiredLanguage || []).find((value) => {
        const normalized = String(value || '')
          .trim()
          .toLowerCase();
        return (
          normalized && !['english', 'japanese', 'chinese'].includes(normalized)
        );
      }) || '';

    const flagsChanged = Object.keys(nextFlags).some(
      (key) => requiredLanguages[key] !== nextFlags[key],
    );

    if (flagsChanged) {
      setRequiredLanguages(nextFlags);
    }
    if (String(otherLanguage || '') !== String(nextOther || '')) {
      setOtherLanguage(nextOther);
    }
  }, [textValues.requiredLanguage, requiredLanguages, otherLanguage]);

  useEffect(() => {
    const nextValues = toNormalizedArray(textValues.applicantNationality || []);
    const currentValues = toNormalizedArray(
      nationalities.map((item) => item.value),
    );

    if (isSameArray(nextValues, currentValues)) {
      return;
    }

    if (nextValues.length === 0) {
      setNationalities([{ value: '', isOpen: false }]);
      setNoNationalitySelection(false);
      return;
    }

    setNationalities(
      nextValues.map((value) => ({
        value,
        isOpen: false,
      })),
    );
    setNoNationalitySelection(false);
  }, [textValues.applicantNationality, nationalities]);

  useEffect(() => {
    if (textValues.topikRequirement !== 'required with minimum level') {
      setTextValues((prev) => ({
        ...prev,
        topikRequirementLevel: '',
      }));
    }
  }, [textValues.topikRequirement, setTextValues]);

  useEffect(() => {
    if (textValues.topikSpeakingRequirement !== 'required with minimum level') {
      setTextValues((prev) => ({
        ...prev,
        topikSpeakingRequirementLevel: '',
      }));
    }
  }, [textValues.topikSpeakingRequirement, setTextValues]);

  return (
    <PostSection title={t('Requirements', '지원 자격')}>
      <PostToggleRow
        label={t('Location', '거주지')}
        helperText={t('Korea residents only', '국내 거주자만 지원 가능')}
        value={textValues.koreaResidentsOnly}
        onChange={(value) => {
          setTextValues((prev) => ({
            ...prev,
            koreaResidentsOnly: value,
          }));
        }}
      />

      <div className='flex flex-col w-full gap-8'>
        <div
          ref={(el) => (refs.current.koreanSkills = el)}
          className='relative'
        >
          <Input
            data-validation-field='koreanSkills'
            label={t('Korean Skills - Level*', '한국어 능력 - 수준*')}
            value={textValues.koreanSkills}
            state={errors.koreanSkills ? 'failed' : 'default'}
            showStatusIcon={false}
            message={errors.koreanSkills}
            placeholder={t(
              'Select required Korean level',
              '필요한 한국어 수준을 선택해 주세요',
            )}
            rightIcon={
              isKoreanSkillsOpen ? (
                <ArrowTopIcon className='text-icon-placeholder' />
              ) : (
                <ArrowBottomIcon className='text-icon-placeholder' />
              )
            }
            readOnly
            onClick={() => setIsKoreanSkillsOpen((prev) => !prev)}
            onRightIconClick={() => setIsKoreanSkillsOpen((prev) => !prev)}
          />
          {isKoreanSkillsOpen && (
            <div className='absolute left-0 z-20 w-full mt-1 top-full'>
              <KoreanSkillsList
                selectedOption={textValues.koreanSkills}
                onSelect={(option) => {
                  setTextValues((prev) => ({
                    ...prev,
                    koreanSkills: option,
                  }));
                  setIsKoreanSkillsOpen(false);
                }}
              />
            </div>
          )}
        </div>
        <div>
          <TopikRadioGroup
            label={t('TOPIK Requirement (optional)', 'TOPIK 요건 (선택)')}
            value={textValues.topikRequirement}
            level={textValues.topikRequirementLevel}
            onChange={(value) => {
              setTextValues((prev) => ({
                ...prev,
                topikRequirement: value,
              }));
            }}
            onLevelChange={(level) => {
              setTextValues((prev) => ({
                ...prev,
                topikRequirementLevel: level,
              }));
            }}
          />
        </div>

        <div>
          <TopikRadioGroup
            label={t(
              'TOPIK Speaking Certificate (optional)',
              'TOPIK 말하기 성적 (선택)',
            )}
            value={textValues.topikSpeakingRequirement}
            level={textValues.topikSpeakingRequirementLevel}
            onChange={(value) => {
              setTextValues((prev) => ({
                ...prev,
                topikSpeakingRequirement: value,
              }));
            }}
            onLevelChange={(level) => {
              setTextValues((prev) => ({
                ...prev,
                topikSpeakingRequirementLevel: level,
              }));
            }}
          />
        </div>
      </div>

      <div
        ref={(el) => (refs.current.nationality = el)}
        className='flex flex-col w-full gap-2'
      >
        {nationalities.map((item, index) => (
          <div key={index} className='relative'>
            <Input
              data-validation-field={index === 0 ? 'nationality' : undefined}
              label={index === 0 ? t('Nationality*', '국적*') : ''}
              value={item.value}
              state={errors.nationality ? 'failed' : 'default'}
              showStatusIcon={false}
              message={errors.nationality}
              placeholder={t(
                'Select preferred applicant nationality',
                '선호하는 지원자 국적을 선택해 주세요',
              )}
              onChange={(e) => {
                const updated = [...nationalities];
                updated[index].value = e.target.value;
                updated[index].isOpen = true;
                syncApplicantNationalities(updated);
              }}
              onClick={() => {
                const updated = [...nationalities];
                updated[index].isOpen = !updated[index].isOpen;
                setNationalities(updated);
              }}
              rightIcon={
                item.isOpen ? (
                  <ArrowTopIcon className='text-icon-placeholder' />
                ) : (
                  <ArrowBottomIcon className='text-icon-placeholder' />
                )
              }
            />

            {item.isOpen && (
              <div className='absolute top-full mt-0.5 left-0 z-20 w-full'>
                <NationalityAutoCompleteList
                  searchText={item.value}
                  onSelect={(option) => {
                    const updated = [...nationalities];
                    updated[index].value = option;
                    updated[index].isOpen = false;
                    syncApplicantNationalities(updated);
                  }}
                  isJobForm
                />
              </div>
            )}
          </div>
        ))}

        <PostCheckboxRow
          label={t('No selection', '선택 안 함')}
          checked={noNationalitySelection}
          onChange={(e) => {
            const checked = e.target.checked;
            setNoNationalitySelection(checked);

            if (checked) {
              syncApplicantNationalities(
                nationalities.map((item) => ({
                  ...item,
                  value: '',
                  isOpen: false,
                })),
                checked,
              );
            }
          }}
        />

        <button
          type='button'
          onClick={() =>
            syncApplicantNationalities([
              ...nationalities,
              { value: '', isOpen: false },
            ])
          }
          className='py-1.5 self-start typo-b11 text-text-secondary underline underline-offset-2 hover:text-text-primary'
        >
          {t('One more Nationality', '국적 추가')}
        </button>
      </div>

      <div ref={(el) => (refs.current.careerLevel = el)} className='relative'>
        <Input
          data-validation-field='careerLevel'
          label={t('Career Level', '경력 수준')}
          value={
            textValues.careerLevel === 'Select desired experience level'
              ? ''
              : textValues.careerLevel
          }
          state={errors.careerLevel ? 'failed' : 'default'}
          showStatusIcon={false}
          message={errors.careerLevel}
          placeholder={ph(
            t(
              'Select desired experience level',
              '희망 경력 수준을 선택해 주세요',
            ),
          )}
          rightIcon={
            isCareerLevelOpen ? (
              <ArrowTopIcon className='text-icon-placeholder' />
            ) : (
              <ArrowBottomIcon className='text-icon-placeholder' />
            )
          }
          readOnly
          onClick={() => setIsCareerLevelOpen((prev) => !prev)}
          onRightIconClick={() => setIsCareerLevelOpen((prev) => !prev)}
        />
        {isCareerLevelOpen && (
          <div className='absolute left-0 z-20 w-full mt-1 top-full'>
            <CareerLevelList
              options={careerLevelOptions}
              selectedOption={textValues.careerLevel}
              onSelect={(option) => {
                setTextValues((prev) => ({
                  ...prev,
                  careerLevel: option,
                }));
                setIsCareerLevelOpen(false);
              }}
            />
          </div>
        )}
      </div>

      <div
        data-validation-field='requiredLanguage'
        className='flex flex-col w-full gap-2'
      >
        <div className='flex items-center gap-0.5'>
          <span className='typo-b12Strong tabletH:typo-b16 text-text-primary'>
            {t('Required Language', '필요 언어')}
          </span>
          <span className='typo-b16Strong text-text-accent'>*</span>
        </div>

        <PostCheckboxRow
          label={t('English', '영어')}
          checked={requiredLanguages.english}
          onChange={handleLanguageToggle('english')}
        />
        <PostCheckboxRow
          label={t('Japanese', '일본어')}
          checked={requiredLanguages.japanese}
          onChange={handleLanguageToggle('japanese')}
        />
        <PostCheckboxRow
          label={t('Chinese', '중국어')}
          checked={requiredLanguages.chinese}
          onChange={handleLanguageToggle('chinese')}
        />
        <PostCheckboxRow
          label={t('Other', '기타')}
          checked={requiredLanguages.other}
          onChange={handleLanguageToggle('other')}
        >
          {requiredLanguages.other && (
            <div
              ref={(el) => (refs.current.language = el)}
              className='relative'
            >
              <Input
                label=''
                placeholder={t(
                  'Search and add language',
                  '언어를 검색해서 추가해 주세요',
                )}
                value={otherLanguage}
                state={errors.requiredLanguage ? 'failed' : 'default'}
                message={errors.requiredLanguage}
                onChange={(e) => {
                  const nextLanguage = e.target.value;
                  setOtherLanguage(nextLanguage);
                  setIsLanguageOpen(true);
                  setTextValues((prev) => ({
                    ...prev,
                    requiredLanguage: getSelectedRequiredLanguages(
                      requiredLanguages,
                      nextLanguage,
                    ),
                  }));
                }}
                rightIcon={
                  isLanguageOpen ? (
                    <ArrowTopIcon className='text-icon-placeholder' />
                  ) : (
                    <ArrowBottomIcon className='text-icon-placeholder' />
                  )
                }
                onClick={() => setIsLanguageOpen((prev) => !prev)}
                onRightIconClick={() => setIsLanguageOpen((prev) => !prev)}
              />
              {isLanguageOpen && (
                <div className='absolute top-full mt-0.5 left-0 z-20 w-full'>
                  <LanguageList
                    searchText={otherLanguage}
                    selectedIso=''
                    onSelect={(option) => {
                      syncRequiredLanguageValues(
                        requiredLanguages,
                        option.name,
                      );
                      setIsLanguageOpen(false);
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </PostCheckboxRow>
        <PostCheckboxRow
          label={t('No selection', '선택 안 함')}
          checked={requiredLanguages.noSelection}
          onChange={handleLanguageToggle('noSelection')}
        />

        {errors.requiredLanguage && (
          <div className='flex text-text-failed typo-b14Strong gap-0.5'>
            <InfoIcon />
            {errors.requiredLanguage}
          </div>
        )}
      </div>

      <PostRadioGroup
        label={t('Start availability (optional)', '입사 가능 시기 (선택)')}
        disabled
        value='yes'
        options={[
          { value: 'yes', label: t('Yes', '예') },
          { value: 'no', label: t('No', '아니요') },
        ]}
      />

      <PostToggleRow
        label={t('Portfolio submission', '포트폴리오 제출')}
        helperText={t(
          'Portfolio is required for this job',
          '이 공고는 포트폴리오 제출이 필수입니다',
        )}
        value={textValues.portfolioSubmission}
        onChange={(value) => {
          setTextValues((prev) => ({
            ...prev,
            portfolioSubmission: value,
          }));
        }}
      />

      <PostToggleRow
        label={t('Required Application Information', '필수 지원 서류')}
        helperText={t(
          'Resume is required for this job',
          '이 공고는 이력서 제출이 필수입니다',
        )}
        value={textValues.resumeRequired}
        onChange={(value) => {
          setTextValues((prev) => ({
            ...prev,
            resumeRequired: value,
          }));
        }}
      />
    </PostSection>
  );
};

export default Requirements;
